import { HTTP_STATUS_INTERNAL_SERVER_ERROR } from '../../../../constants/api'
import { EMPTY_LIST } from '../../../../constants/common'
import { RENTAL_PORTAL_MOCK_STATE } from '../../../../constants/rentalPortalMockState'
import { hasRentalPortalMockState } from '../../../../utils/rentalPortalMockState'

import {
  FIELD_OWNERSHIP_ORIGIN,
  FIELD_OWNERSHIP_OWNER,
  FIELD_OWNERSHIP_PRECEDENCE,
  IMPORT_POLICY_STATUS,
  OWNERSHIP_ENTITY_ALL,
} from './IntegrationsDataSources.constants'
import { getIsFieldOwnerPortal } from './IntegrationsDataSources.utils'

const MOCK_IMPORT_READ_LATENCY = 320

const MOCK_IMPORT_READ_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_IMPORT_WRITE_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_IMPORT_SOURCE_LABEL = 'RELion ONE'

const MOCK_IMPORT_POLICY = {
  cadence_hours: 4,
  default_shared_precedence: FIELD_OWNERSHIP_PRECEDENCE.holdForReview,
  enabled: true,
  last_run: {
    fields_blocked: 0,
    fields_held: 2,
    records_received: 1284,
    started_at: '2026-09-17T12:05:00+02:00',
  },
  source_label: MOCK_IMPORT_SOURCE_LABEL,
  source_system: 'preloader',
  status: IMPORT_POLICY_STATUS.active,
}

function toMockOwnershipRow([entity, field, owner, precedence, origin]) {
  return {
    entity,
    field,
    locked_in_ui: !getIsFieldOwnerPortal(owner),
    origin: origin ?? FIELD_OWNERSHIP_ORIGIN.default,
    owner,
    shared_precedence: precedence ?? null,
    source_label: MOCK_IMPORT_SOURCE_LABEL,
  }
}

function getMockOwnershipPermissions() {
  return {
    can_edit: !hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.readOnly),
  }
}

function findMockOwnershipRow({ entity, field }) {
  return MOCK_FIELD_OWNERSHIP.find(
    row => row.entity === entity && row.field === field
  )
}

const MOCK_FIELD_OWNERSHIP = [
  ['Flat', 'external_id', FIELD_OWNERSHIP_OWNER.import],
  ['Flat', 'number', FIELD_OWNERSHIP_OWNER.import],
  ['Flat', 'area', FIELD_OWNERSHIP_OWNER.import],
  ['Flat', 'room_count', FIELD_OWNERSHIP_OWNER.import],
  ['Flat', 'floor', FIELD_OWNERSHIP_OWNER.import],
  [
    'Flat',
    'info',
    FIELD_OWNERSHIP_OWNER.shared,
    FIELD_OWNERSHIP_PRECEDENCE.holdForReview,
    FIELD_OWNERSHIP_ORIGIN.tenant,
  ],
  ['Flat', 'rent_gross', FIELD_OWNERSHIP_OWNER.import],
  ['Building', 'external_id', FIELD_OWNERSHIP_OWNER.import],
  ['Building', 'address', FIELD_OWNERSHIP_OWNER.import],
  ['Building', 'build_year', FIELD_OWNERSHIP_OWNER.import],
  ['Building', 'unit_count', FIELD_OWNERSHIP_OWNER.import],
  [
    'Building',
    'info',
    FIELD_OWNERSHIP_OWNER.shared,
    FIELD_OWNERSHIP_PRECEDENCE.importWins,
  ],
  ['Listing', 'rent_price', FIELD_OWNERSHIP_OWNER.portal],
  ['Listing', 'description', FIELD_OWNERSHIP_OWNER.portal],
  ['Listing', 'available_from', FIELD_OWNERSHIP_OWNER.portal],
  ['Listing', 'photos', FIELD_OWNERSHIP_OWNER.portal],
  ['Listing', 'portals', FIELD_OWNERSHIP_OWNER.portal],
  ['Listing', 'title', FIELD_OWNERSHIP_OWNER.portal],
  ['Listing', 'deposit_amount', FIELD_OWNERSHIP_OWNER.portal],
  ['Listing', 'service_charge', FIELD_OWNERSHIP_OWNER.portal],
  ['Contract', 'external_id', FIELD_OWNERSHIP_OWNER.import],
  ['Contract', 'start_date', FIELD_OWNERSHIP_OWNER.import],
  ['Contract', 'end_date', FIELD_OWNERSHIP_OWNER.import],
  ['Contract', 'rent_net', FIELD_OWNERSHIP_OWNER.import],
  [
    'Contract',
    'note',
    FIELD_OWNERSHIP_OWNER.shared,
    FIELD_OWNERSHIP_PRECEDENCE.portalWins,
  ],
  ['User', 'external_id', FIELD_OWNERSHIP_OWNER.import],
  ['User', 'first_name', FIELD_OWNERSHIP_OWNER.import],
  ['User', 'last_name', FIELD_OWNERSHIP_OWNER.import],
  ['User', 'email', FIELD_OWNERSHIP_OWNER.import],
  [
    'User',
    'phone',
    FIELD_OWNERSHIP_OWNER.shared,
    FIELD_OWNERSHIP_PRECEDENCE.portalWins,
    FIELD_OWNERSHIP_ORIGIN.tenant,
  ],
].map(toMockOwnershipRow)

const MOCK_OWNERSHIP_ENTITIES = [
  ...new Set(MOCK_FIELD_OWNERSHIP.map(row => row.entity)),
]

function withMockLatency() {
  return new Promise(resolve => {
    setTimeout(resolve, MOCK_IMPORT_READ_LATENCY)
  })
}

export function getMockImportPolicy() {
  return withMockLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_IMPORT_READ_ERROR)
    }

    return {
      data: hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
        ? null
        : {
            ...MOCK_IMPORT_POLICY,
            has_running_run: hasRentalPortalMockState(
              RENTAL_PORTAL_MOCK_STATE.importRunning
            ),
            permissions: getMockOwnershipPermissions(),
          },
    }
  })
}

export function getMockFieldOwnershipEntities() {
  return [...MOCK_OWNERSHIP_ENTITIES]
}

export function getMockFieldOwnership({ entity, page, page_size: pageSize }) {
  return withMockLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_IMPORT_READ_ERROR)
    }

    const rows = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
      ? EMPTY_LIST
      : MOCK_FIELD_OWNERSHIP.filter(
          row => entity === OWNERSHIP_ENTITY_ALL || row.entity === entity
        )

    const pageCount = Math.ceil(rows.length / pageSize)
    const currentPage = Math.min(page, Math.max(pageCount, 1))
    const firstIndex = (currentPage - 1) * pageSize

    return {
      data: {
        items: rows
          .slice(firstIndex, firstIndex + pageSize)
          .map(row => ({ ...row, permissions: getMockOwnershipPermissions() })),
        pagination: {
          count: rows.length,
          page: currentPage,
          page_count: pageCount,
          page_size: pageSize,
        },
      },
    }
  })
}

export function patchMockFieldOwnership({
  entity,
  field,
  owner,
  shared_precedence: sharedPrecedence,
}) {
  return withMockLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_IMPORT_WRITE_ERROR)
    }

    const row = findMockOwnershipRow({ entity, field })

    Object.assign(row, {
      locked_in_ui: !getIsFieldOwnerPortal(owner),
      origin: FIELD_OWNERSHIP_ORIGIN.tenant,
      owner,
      shared_precedence: sharedPrecedence ?? null,
    })

    return { data: { ...row, permissions: getMockOwnershipPermissions() } }
  })
}

export function patchMockImportPolicy({
  default_shared_precedence: defaultPrecedence,
  enabled,
}) {
  return withMockLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_IMPORT_WRITE_ERROR)
    }

    Object.assign(MOCK_IMPORT_POLICY, {
      default_shared_precedence: defaultPrecedence,
      enabled,
      status: enabled
        ? IMPORT_POLICY_STATUS.active
        : IMPORT_POLICY_STATUS.paused,
    })

    return { data: { ...MOCK_IMPORT_POLICY } }
  })
}
